/**
 * Shared conversation-history helpers for the Agent SDK "door" pages (Quality
 * Co-Pilot, Tech Support Co-Pilot, …) — pure + unit-testable.
 *
 * The active conversation is remembered PER USER under a per-door key prefix
 * (e.g. `cm_qcp_convid_{userId}`, `cm_tsc_convid_{userId}`) so a page refresh
 * resumes the last thread and one browser shared by two staff never crosses
 * streams — the key changes with the logged-in user, and a missing/blank user
 * falls back to a shared `anon` bucket. localStorage access is wrapped so a
 * privacy mode / quota error degrades to a no-op (refresh-resume just doesn't
 * happen) rather than throwing.
 */

export interface ActiveConvStore {
  /** Per-user localStorage key for this door's active conversation id. */
  key(userId: string | null | undefined): string;
  /** Read the remembered id (null if none / storage blocked). Never throws. */
  read(userId: string | null | undefined): string | null;
  /** Persist (or, with null, clear) the id for this user. Never throws. */
  write(userId: string | null | undefined, conversationId: string | null): void;
}

/**
 * Build a per-user active-conversation store for one door. `prefix` is the
 * localStorage key prefix, INCLUDING the trailing underscore — e.g.
 * `'cm_tsc_convid_'`.
 */
export function makeActiveConvStore(prefix: string): ActiveConvStore {
  const keyFor = (userId: string | null | undefined): string =>
    `${prefix}${userId || 'anon'}`;
  return {
    key: keyFor,
    read(userId) {
      try {
        return window.localStorage.getItem(keyFor(userId));
      } catch {
        return null;
      }
    },
    write(userId, conversationId) {
      try {
        const k = keyFor(userId);
        if (conversationId) {
          window.localStorage.setItem(k, conversationId);
        } else {
          window.localStorage.removeItem(k);
        }
      } catch {
        /* storage unavailable (private mode / quota) — refresh-resume no-ops */
      }
    },
  };
}

/**
 * Compact relative time for the history list ("just now", "5m", "3h", "2d"),
 * falling back to a short localized date beyond a week. `nowMs` is injectable so
 * the formatting is deterministic in tests. Invalid/empty input returns ''.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  nowMs?: number,
): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const now = nowMs ?? Date.now();
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(then).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
